import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill, SITE_LABEL } from "@/components/Layout";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const LEAVE_LABELS = {
  ANNUAL: "Annual Holidays",
  SICK: "Sick Leave",
  BEREAVEMENT: "Bereavement Leave",
  PARENTAL: "Parental Leave",
  ALTERNATIVE_HOLIDAYS: "Alternative Holidays",
  PUBLIC_HOLIDAY: "Public Holiday",
  UNPAID: "Unpaid Leave",
  TOIL: "Time in Lieu",
};

function fmtWeekEnding(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

function fmtAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LABELS = (days) => (days || []).map((d) => DAY_NAMES[d]).join(", ");

function LeaveForm({ balances, onSubmitted }) {  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ leave_type: "ANNUAL", start_date: today, end_date: today, description: "" });
  const [periods, setPeriods] = useState([]); // [{week_start, week_ending, suggested_hours, hours}]
  const [pattern, setPattern] = useState(null);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Current balance for the selected leave type — shown inline a la Xero.
  // NOTE: since iter20+ `entitled_days`/`used_days` natively store HOURS
  // (synced from Xero's `balance` field), so no day→hour conversion needed.
  const currentBalance = balances.find((b) => b.leave_type === form.leave_type);
  const balanceHours = currentBalance
    ? Math.max(0, currentBalance.entitled_days - currentBalance.used_days)
    : null;

  // Fetch the per-week pay-period breakdown from the backend whenever dates change.
  const fetchPeriods = useCallback(async (start, end) => {
    if (!start || !end) { setPeriods([]); return; }
    if (end < start) { setPeriods([]); return; }
    setLoadingPeriods(true);
    try {
      const { data } = await api.get("/leave/pay-periods", { params: { start_date: start, end_date: end } });
      setPeriods(data.periods.map((p) => ({ ...p, hours: p.suggested_hours })));
      setPattern(data.pattern || null);
    } catch {
      setPeriods([]);
    } finally {
      setLoadingPeriods(false);
    }
  }, []);

  useEffect(() => { fetchPeriods(form.start_date, form.end_date); }, [fetchPeriods, form.start_date, form.end_date]);

  const totalHours = periods.reduce((s, p) => s + (Number(p.hours) || 0), 0);
  const overBalance = balanceHours != null && totalHours > balanceHours
    && ["ANNUAL", "SICK", "BEREAVEMENT", "PARENTAL", "ALTERNATIVE_HOLIDAYS", "TOIL"].includes(form.leave_type);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leave", {
        leave_type: form.leave_type,
        start_date: form.start_date,
        end_date: form.end_date,
        description: form.description,
        periods: periods.map((p) => ({ week_ending: p.week_ending, hours: Number(p.hours) || 0 })),
      });
      toast.success("Leave request submitted");
      setOpen(false);
      setForm({ leave_type: "ANNUAL", start_date: today, end_date: today, description: "" });
      setPeriods([]);
      onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#1A2744] hover:bg-[#263A63] text-white" data-testid="apply-leave-button">
          New leave request
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New leave request</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="apply-leave-form">
          <div>
            <Label>Leave type</Label>
            <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
              <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(LEAVE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`leave-type-${k}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description <span className="text-gray-400 text-xs">(optional)</span></Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="E.g. Family holiday to Queenstown"
              data-testid="leave-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="leave-start-date" />
            </div>
            <div>
              <Label>End date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required data-testid="leave-end-date" />
            </div>
          </div>

          {/* Current balance — Xero-style inline readout */}
          {balanceHours != null && (
            <div className="border border-gray-200 rounded-md">
              <div className="flex justify-between items-center px-3 py-2 border-b border-gray-100">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Current Leave Balance</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Hours</span>
              </div>
              <div className="flex justify-between items-center px-3 py-2">
                <span className="text-sm text-[#1A2744]">{LEAVE_LABELS[form.leave_type]}</span>
                <span className="text-sm font-semibold text-[#1A2744]" data-testid="leave-current-balance">{balanceHours.toFixed(1)}</span>
              </div>
            </div>
          )}

          {/* Pay-period editable breakdown */}
          {periods.length > 0 && (
            <div className="border border-gray-200 rounded-md" data-testid="leave-pay-periods">
              <div className="flex justify-between items-center px-3 py-2 border-b border-gray-100">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Pay Period</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Hours</span>
              </div>
              {periods.map((p, i) => (
                <div key={p.week_ending} className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 border-gray-100 gap-3">
                  <span className="text-sm text-[#1A2744]">Week ending {fmtWeekEnding(p.week_ending)}</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.25"
                    className="w-24 h-9 text-right"
                    value={p.hours}
                    onChange={(ev) => {
                      const next = [...periods];
                      next[i] = { ...next[i], hours: ev.target.value };
                      setPeriods(next);
                    }}
                    data-testid={`leave-period-hours-${p.week_ending}`}
                  />
                </div>
              ))}
              <div className="flex justify-between items-center px-3 py-2 border-t-2 border-gray-200 bg-gray-50">
                <span className="text-sm font-semibold text-[#1A2744]">Total</span>
                <span className="text-sm font-bold text-[#1A2744]" data-testid="leave-total-hours">{totalHours.toFixed(2)}h</span>
              </div>
              {pattern && (
                <p className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-100" data-testid="leave-hours-basis">
                  {pattern.weekly_hours > 0 ? (
                    <>Auto-filled from your contracted {pattern.weekly_hours}h week ({pattern.daily_hours}h on each of your work days: {DAY_LABELS(pattern.work_days)}). Edit any week if it's different.</>
                  ) : (
                    <>No contracted hours on file yet — enter your hours for each week and an admin will confirm them.</>
                  )}
                </p>
              )}
            </div>
          )}

          {overBalance && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="leave-over-balance">
              Heads-up — this request exceeds your available {LEAVE_LABELS[form.leave_type]} balance ({balanceHours.toFixed(1)}h). Your manager can still approve it at their discretion.
            </p>
          )}

          <DialogFooter>
            <Button type="submit" disabled={submitting || loadingPeriods} className="bg-[#1A2744] text-white" data-testid="leave-submit">
              {submitting ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Leave() {
  const [balances, setBalances] = useState([]);
  const [mine, setMine] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/leave/balances"),
      api.get("/leave/mine"),
      api.get("/leave/calendar"),
    ]);
    setBalances(a.data.balances);
    setMine(b.data.leave);
    setCalendar(c.data.leave);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  // Most-recent xero_last_synced_at across the visible balances. Empty
  // string → never synced (UI shows the "Sync from Xero" CTA without an
  // accompanying "as of …" caption).
  const xeroSyncedAt = useMemo(() => {
    return balances.reduce((latest, b) => {
      const ts = b.xero_last_synced_at || "";
      return ts && ts > latest ? ts : latest;
    }, "");
  }, [balances]);

  const syncFromXero = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/leave/balances/sync-mine");
      if (data.status === "OK") {
        const n = (data.synced || []).length;
        toast.success(n ? `Refreshed ${n} balance${n === 1 ? "" : "s"} from Xero.` : "Synced — no balances on file in Xero yet.");
        await load();
      } else if (data.status === "SKIPPED_NO_XERO_EMPLOYEE") {
        toast.message("Your Xero employee profile isn't linked yet — ask your manager.");
      } else if (data.status === "FAILED") {
        toast.error(data.error || "Couldn't reach Xero. Try again in a moment.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sync from Xero.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="leave-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">Leave</h1>
          <p className="text-gray-500 mt-1">Submit requests and track your balances.</p>
        </div>
        <LeaveForm balances={balances} onSubmitted={load} />
      </header>

      <Card className="border-gray-200" data-testid="leave-balances-card">
        <CardContent className="p-4 sm:p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold">Leave balances</div>
              <div className="text-xs text-gray-500 mt-0.5" data-testid="leave-balances-synced-at">
                {xeroSyncedAt
                  ? `Synced from Xero · ${fmtAgo(xeroSyncedAt)}`
                  : "Not yet synced from Xero"}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={syncFromXero}
              disabled={syncing}
              data-testid="leave-balances-refresh"
              className="gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Refresh from Xero"}
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {balances
              .filter((b) => ["ANNUAL", "SICK", "ALTERNATIVE_HOLIDAYS"].includes(b.leave_type))
              .map((b) => (
              <Card key={b.leave_type} className="border-gray-200">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                    {LEAVE_LABELS[b.leave_type]}
                  </div>
                  <div className="font-heading text-2xl font-bold text-[#1A2744] mt-2" data-testid={`balance-${b.leave_type}`}>
                    {(b.entitled_days - b.used_days).toFixed(1)}
                    <span className="text-sm font-normal text-gray-500 ml-1">hrs</span>
                  </div>
                  <div className="text-[11px] text-gray-500">of {Number(b.entitled_days || 0).toFixed(1)} hrs</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="mine">
        <TabsList>
          <TabsTrigger value="mine" data-testid="leave-tab-mine">My requests</TabsTrigger>
          <TabsTrigger value="team" data-testid="leave-tab-team">Team calendar</TabsTrigger>
        </TabsList>
        <TabsContent value="mine" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="md:hidden divide-y divide-gray-100 px-4" data-testid="leave-mine-cards">
                {mine.length === 0 && <p className="py-8 text-center text-gray-500">No requests yet.</p>}
                {mine.map((l) => (
                  <div key={l.id} className="py-3 space-y-1" data-testid={`leave-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-[#1A2744]">{LEAVE_LABELS[l.leave_type]}</p>
                        <p className="text-sm text-gray-600">{l.start_date} → {l.end_date}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <StatusPill status={l.status} />
                        <p className="mt-1 text-sm font-semibold text-[#1A2744]">
                          {l.total_hours != null ? `${Number(l.total_hours).toFixed(1)}h` : (l.days ?? "—")}
                        </p>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-400">
                      Submitted {new Date(l.created_at).toLocaleDateString()}
                    </p>
                    {l.admin_note && <p className="text-xs text-gray-500">Admin: {l.admin_note}</p>}
                  </div>
                ))}
              </div>
              <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Admin note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mine.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No requests yet.</TableCell></TableRow>
                  )}
                  {mine.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs text-gray-500">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>{LEAVE_LABELS[l.leave_type]}</TableCell>
                      <TableCell>{l.start_date} → {l.end_date}</TableCell>
                      <TableCell>{l.total_hours != null ? `${Number(l.total_hours).toFixed(1)}` : (l.days ?? "—")}</TableCell>
                      <TableCell><StatusPill status={l.status} /></TableCell>
                      <TableCell className="text-xs text-gray-500">{l.admin_note || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="team" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="md:hidden divide-y divide-gray-100 px-4" data-testid="leave-team-cards">
                {calendar.length === 0 && <p className="py-8 text-center text-gray-500">Nothing booked.</p>}
                {calendar.map((l) => (
                  <div key={l.id} className="py-3 flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-[#1A2744]">{l.user_name}</p>
                      <p className="text-sm text-gray-600">{l.start_date} → {l.end_date}</p>
                      <p className="text-[11px] text-gray-400">
                        {LEAVE_LABELS[l.leave_type]} · {SITE_LABEL[l.site]}
                      </p>
                    </div>
                    <StatusPill status={l.status} />
                  </div>
                ))}
              </div>
              <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Who</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calendar.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">Nothing booked.</TableCell></TableRow>
                  )}
                  {calendar.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.user_name}</TableCell>
                      <TableCell className="text-xs text-gray-500">{SITE_LABEL[l.site]}</TableCell>
                      <TableCell>{LEAVE_LABELS[l.leave_type]}</TableCell>
                      <TableCell>{l.start_date} → {l.end_date}</TableCell>
                      <TableCell><StatusPill status={l.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
